import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { Clock, CreditCard, ArrowRight, Check } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

const Register = ({ onRegister }) => {
  const navigate = useNavigate();
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [gender, setGender] = useState('');
  const [universidad, setUniversidad] = useState('');
  const [profileImage, setProfileImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [price, setPrice] = useState(1500);
  const [showChoice, setShowChoice] = useState(false);
  const [registeredUserId, setRegisteredUserId] = useState(null);
  const [payLoading, setPayLoading] = useState(false);

  React.useEffect(() => {
    axios.get(`${API_BASE_URL}/api/settings/price`)
      .then(res => setPrice(res.data.price))
      .catch(() => {});
  }, []);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('La imagen es muy grande. Maximo 5MB.');
        return;
      }
      
      setProfileImage(file);
      
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!fullName || !email || !password || !confirmPassword) {
      toast.error('Por favor complete todos los campos');
      return;
    }

    if (!gender) {
      toast.error('Por favor seleccione su genero');
      return;
    }

    if (!universidad || universidad.trim() === '') {
      toast.error('Por favor ingrese el nombre de su universidad');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Las contrasenas no coinciden');
      return;
    }

    if (password.length < 6) {
      toast.error('La contrasena debe tener al menos 6 caracteres');
      return;
    }

    if (!acceptedTerms) {
      toast.error('Debes aceptar los Terminos y Condiciones para continuar');
      return;
    }

    setLoading(true);
    const result = await onRegister(fullName, email, password, profileImage, gender, universidad);
    
    if (result.success) {
      setRegisteredUserId(result.user_id);
      setShowChoice(true);
      toast.success('Cuenta creada exitosamente');
    } else {
      toast.error(result.message);
    }
    
    setLoading(false);
  };

  const handlePayNow = async () => {
    if (!registeredUserId) return;
    setPayLoading(true);
    try {
      const paymentResponse = await axios.post(`${API_BASE_URL}/api/payments/create-preference`, {
        user_id: registeredUserId
      });
      const { init_point } = paymentResponse.data;
      if (init_point) {
        window.location.href = init_point;
        return;
      }
      toast.error('Error al crear el pago. Intenta mas tarde.');
    } catch (err) {
      console.error('Payment preference error:', err);
      toast.error('Error al conectar con MercadoPago. Puedes pagar despues desde el inicio de sesion.');
    }
    setPayLoading(false);
  };

  const handleFreeTrial = () => {
    navigate('/login');
  };

  // Post-registration choice screen
  if (showChoice) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-white">
        <div className="w-full max-w-md">
          <div className="rounded-2xl p-8 bg-white" data-testid="registration-choice-screen">
            <div className="flex justify-center mb-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_13d67352-1aa7-430c-b46e-966b83d4fc8c/artifacts/kmnn6rz3_proyectoresident.png"
                alt="Proyecto Residente"
                className="w-60 max-w-full h-auto"
              />
            </div>

            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check size={32} className="text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-black mb-2">
                Registro exitoso
              </h2>
              <p className="text-gray-600">
                Elige como quieres empezar:
              </p>
            </div>

            {/* Option 1: Free Trial */}
            <button
              onClick={handleFreeTrial}
              data-testid="choose-free-trial-btn"
              className="w-full mb-4 p-5 border-2 border-gray-200 rounded-xl hover:border-black transition-all text-left group"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-black group-hover:text-white transition-all">
                  <Clock size={24} />
                </div>
                <div>
                  <h3 className="font-bold text-black text-lg">Prueba gratuita</h3>
                  <p className="text-gray-500 text-sm mt-1">
                    Acceso completo por 72 horas. Sin compromiso.
                  </p>
                </div>
              </div>
            </button>

            {/* Option 2: Pay Now */}
            <button
              onClick={handlePayNow}
              disabled={payLoading}
              data-testid="choose-pay-now-btn"
              className="w-full p-5 bg-black text-white rounded-xl hover:bg-gray-800 transition-all text-left"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <CreditCard size={24} />
                </div>
                <div>
                  <h3 className="font-bold text-lg">
                    {payLoading ? 'Redirigiendo...' : 'Pagar ahora'}
                  </h3>
                  <p className="text-gray-300 text-sm mt-1">
                    ${price.toLocaleString('es-MX')} MXN - 6 meses de acceso completo
                  </p>
                </div>
              </div>
            </button>

            <p className="text-xs text-gray-400 text-center mt-6">
              Con la prueba gratuita podras usar todas las funcionalidades. Al terminar, podras pagar para continuar.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-white">
      <div className="w-full max-w-md">
        <div className="rounded-2xl p-8 bg-white">
          {/* Logo */}
          <div className="flex justify-center mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_13d67352-1aa7-430c-b46e-966b83d4fc8c/artifacts/kmnn6rz3_proyectoresident.png"
              alt="Proyecto Residente"
              className="w-80 max-w-full h-auto"
            />
          </div>

          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-black mb-2">
              Crear Cuenta
            </h2>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nombre Completo</label>
              <input
                type="text"
                data-testid="register-fullname-input"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-black transition-colors bg-white text-black"
                placeholder="Dr. Juan Perez"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Genero</label>
              <div className="flex gap-3">
                <button
                  type="button"
                  data-testid="register-gender-male"
                  onClick={() => setGender('male')}
                  className={`flex-1 py-3 px-4 border rounded-lg font-medium transition-all ${
                    gender === 'male'
                      ? 'border-black bg-black text-white'
                      : 'border-gray-300 bg-white text-gray-700 hover:border-gray-500'
                  }`}
                >
                  Hombre
                </button>
                <button
                  type="button"
                  data-testid="register-gender-female"
                  onClick={() => setGender('female')}
                  className={`flex-1 py-3 px-4 border rounded-lg font-medium transition-all ${
                    gender === 'female'
                      ? 'border-black bg-black text-white'
                      : 'border-gray-300 bg-white text-gray-700 hover:border-gray-500'
                  }`}
                >
                  Mujer
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Universidad (obligatorio)</label>
              <input
                type="text"
                data-testid="register-universidad-input"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-black transition-colors bg-white text-black"
                placeholder="Ej: Universidad Nacional Autonoma de Mexico"
                value={universidad}
                onChange={(e) => setUniversidad(e.target.value)}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Correo Electronico</label>
              <input
                type="email"
                data-testid="register-email-input"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-black transition-colors bg-white text-black"
                placeholder="tu@correo.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Contrasena</label>
              <input
                type="password"
                data-testid="register-password-input"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-black transition-colors bg-white text-black"
                placeholder="********"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Confirmar Contrasena</label>
              <input
                type="password"
                data-testid="register-confirm-password-input"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-black transition-colors bg-white text-black"
                placeholder="********"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Foto de Perfil (Opcional)</label>
              <input
                type="file"
                data-testid="register-profile-image-input"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-black transition-colors bg-white text-black"
                accept="image/*"
                onChange={handleImageChange}
              />
              {imagePreview && (
                <div className="mt-3 text-center">
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="w-20 h-20 rounded-full object-cover mx-auto border-2 border-black"
                  />
                  <p className="text-sm text-gray-600 mt-2">Vista previa</p>
                </div>
              )}
            </div>

            {/* Legal acceptance checkbox */}
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="accept-terms"
                data-testid="register-accept-terms-checkbox"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-gray-300 text-black focus:ring-black cursor-pointer flex-shrink-0"
              />
              <label htmlFor="accept-terms" className="text-xs text-gray-600 leading-relaxed cursor-pointer">
                He leido y acepto los{' '}
                <Link to="/terminos" target="_blank" className="text-black underline hover:text-gray-600">Terminos y Condiciones</Link>,{' '}
                el <Link to="/privacidad" target="_blank" className="text-black underline hover:text-gray-600">Aviso de Privacidad</Link>,{' '}
                el <Link to="/descargo" target="_blank" className="text-black underline hover:text-gray-600">Descargo de Responsabilidad Medica y Educativa</Link>,{' '}
                la <Link to="/pagos-politica" target="_blank" className="text-black underline hover:text-gray-600">Politica de Pagos y Suscripciones</Link>{' '}
                y la <Link to="/propiedad-intelectual" target="_blank" className="text-black underline hover:text-gray-600">Politica de Propiedad Intelectual</Link> de Proyecto Residente.
              </label>
            </div>

            <button
              type="submit"
              data-testid="register-submit-button"
              className="w-full py-3 px-4 bg-black text-white font-semibold rounded-lg hover:bg-gray-800 transition-colors flex items-center justify-center"
              disabled={loading}
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Creando cuenta...
                </>
              ) : (
                'Crear Cuenta'
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-gray-600">
              Ya tienes cuenta?{' '}
              <Link 
                to="/login" 
                data-testid="login-link"
                className="text-black hover:text-gray-600 font-semibold transition-colors"
              >
                Inicia sesion aqui
              </Link>
            </p>
          </div>

          <div className="mt-6 p-4 bg-gray-100 rounded-lg border border-gray-300">
            <p className="text-sm text-black text-center">
              <strong>Prueba gratuita de 72 horas</strong> con acceso completo. Despues: ${price.toLocaleString('es-MX')} MXN por 6 meses.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
